import React, { useCallback, useMemo, useState } from 'react';
import classname from 'classname';
import { Typography } from '@weave-mui/material';

/**
 * CollapsibleContent
 *
 * Generic collapsible section with a clickable title.
 *
 * Performance note:
 * - If `children` is a function, it will only be invoked when the section is open.
 *   This enables lazy rendering for heavy content (e.g. large tables).
 *
 * @param {Object} props
 * @param {React.ReactNode} [props.title=''] - Header content
 * @param {React.ReactNode | Function} props.children - Body content, or a function returning body content
 * @returns {JSX.Element}
 */
const CollapsibleContent = ({ title = '', children }) => {
  const [open, setOpen] = useState(false);

  const handleTitleClick = useCallback(() => {
    setOpen((prev) => !prev);
  }, []);

  const sectionClassName = useMemo(
    () =>
      classname({
        'collapsable-content': true,
        open,
      }),
    [open]
  );

  const hintText = open ? 'collapse' : 'expand';
  const content = typeof children === 'function' ? (open ? children() : null) : children;

  return (
    <section
      className={sectionClassName}
      style={{ width: '100%', maxWidth: '100%', minWidth: 0, overflowX: 'hidden' }}
    >
      <div
        className="collapsable-content-title"
        onClick={handleTitleClick}
      >
        <Typography variant="subtitle1" color="textPrimary">
          {title}
        </Typography>
      </div>

      <Typography variant="caption" color="textSecondary" className="collapsable-content-info" sx={{ marginRight: 2 }}>
        click to {hintText}
      </Typography>

      <div
        className="collapsable-content-body"
        style={{
          width: '100%',
          maxWidth: '100%',
          minWidth: 0,
          overflowX: 'hidden',
          overflowY: 'visible',
        }}
      >
        {content}
      </div>
    </section>
  );
};

export default CollapsibleContent;
